import api from './api'
import type { Estimate, LineItem, EstimateNote, CreateEstimatePayload, CreateLineItemPayload } from '@/types'

export const estimatesService = {
  // Estimates CRUD
  // NOTE: `take` default is intentionally large. The list page paginates client-side
  // (slices the array by PAGE_SIZE=25), so the request must fetch enough records to
  // cover every page the user can navigate to. The old default of 50 capped the UI
  // at exactly 2 pages regardless of how many estimates the tenant actually had.
  // TODO: switch to server-side pagination — refetch on page change using the API's
  // `total` field for the page count, then drop this default back to a sane window.
  async list(status?: string, skip = 0, take = 1000) {
    const params = new URLSearchParams()
    if (status) params.set('status', status)
    params.set('skip', String(skip))
    params.set('take', String(take))
    const { data } = await api.get(`/estimates?${params}`)
    return data.data as { estimates: Estimate[]; total: number }
  },

  async getById(id: string) {
    const { data } = await api.get(`/estimates/${id}`)
    return data.data as Estimate
  },

  async create(payload: CreateEstimatePayload) {
    const { data } = await api.post('/estimates', payload)
    return data.data as Estimate
  },

  async update(id: string, payload: Partial<CreateEstimatePayload & { scopeText?: string; scopeData?: string; defaultMarkup?: number; addedCategories?: string; subcontractorsData?: any; optionsData?: any }>) {
    const { data } = await api.put(`/estimates/${id}`, payload)
    return data.data as Estimate
  },

  async remove(id: string) {
    await api.delete(`/estimates/${id}`)
  },

  async updateStatus(id: string, status: string, rejectionReason?: string) {
    const { data } = await api.put(`/estimates/${id}/status`, { status, rejectionReason })
    return data.data as Estimate
  },

  // Line Items
  async addLineItem(estimateId: string, payload: CreateLineItemPayload) {
    const { data } = await api.post(`/estimates/${estimateId}/line-items`, payload)
    return data.data as LineItem
  },

  async updateLineItem(estimateId: string, itemId: string, payload: Partial<CreateLineItemPayload & { sortOrder?: number }>) {
    const { data } = await api.put(`/estimates/${estimateId}/line-items/${itemId}`, payload)
    return data.data as LineItem
  },

  async deleteLineItem(estimateId: string, itemId: string) {
    await api.delete(`/estimates/${estimateId}/line-items/${itemId}`)
  },

  async reorderLineItems(estimateId: string, itemIds: string[]) {
    const { data } = await api.post(`/estimates/${estimateId}/line-items/reorder`, { itemIds })
    return data.data as LineItem[]
  },

  // Notes
  async addNote(estimateId: string, content: string) {
    const { data } = await api.post(`/estimates/${estimateId}/notes`, { content })
    return data.data as EstimateNote
  },

  async getNotes(estimateId: string) {
    const { data } = await api.get(`/estimates/${estimateId}/notes`)
    return data.data as EstimateNote[]
  },

  async downloadPdf(estimateId: string): Promise<Blob> {
    const { data } = await api.get(`/estimates/${estimateId}/pdf`, { responseType: 'blob' })
    return data as Blob
  },

  // Field notes (from mobile app + desktop)
  async getFieldNotes(estimateId: string) {
    const { data } = await api.get(`/estimates/${estimateId}/field-notes`)
    return data.data as FieldNote[]
  },

  async addFieldNote(estimateId: string, text: string) {
    const { data } = await api.post(`/estimates/${estimateId}/field-notes`, { text })
    return data.data as FieldNote
  },

  async deleteFieldNote(estimateId: string, noteId: string) {
    await api.delete(`/estimates/${estimateId}/field-notes/${noteId}`)
  },

  // Map pins (from mobile app)
  async getMapPins(estimateId: string) {
    const { data } = await api.get(`/estimates/${estimateId}/map-pins`)
    return data.data as MapPin[]
  },

  async duplicate(id: string) {
    const { data } = await api.post(`/estimates/${id}/duplicate`, {})
    return data.data as Estimate
  },

  // Presence
  async pingPresence(estimateId: string) {
    await api.post(`/estimates/${estimateId}/presence`, {})
  },
  async getPresence(estimateId: string) {
    const { data } = await api.get(`/estimates/${estimateId}/presence`)
    return data.data as PresenceUser[]
  },
  async leavePresence(estimateId: string) {
    await api.delete(`/estimates/${estimateId}/presence`)
  },
}

export interface FieldNote {
  id: string
  estimateId: string
  authorId: string
  authorName: string
  text: string
  photoAssetId: string | null
  photoUrl: string | null
  createdAt: string
  updatedAt: string
  /** Written on the estimate this one revises — shown here, but read-only. */
  inherited?: boolean
}

export interface PresenceUser {
  userId: string
  displayName: string
  lastSeenAt: string
}

export interface MapPin {
  id: string
  estimateId: string
  latitude: number
  longitude: number
  label: string | null
  noteText: string | null
  photoUrl: string | null
  photoAssetId: string | null
  createdAt: string
}
